import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { InviteCountdown } from "@/components/InviteCountdown";
import {
  Building2, Plus, Upload, Download, FileText, Loader2, Trash2, Pencil,
  Send, Copy, Search, Mail, Globe, MailCheck, MailWarning, MailX,
} from "lucide-react";

const STATUS_LABELS = {
  a_contacter: "À contacter", contacte: "Contacté", relance: "Relancé",
  interesse: "Intéressé", publie: "Publié", refuse: "Refusé",
};
const STATUS_COLORS = {
  a_contacter: "bg-white/10 text-muted-foreground",
  contacte: "bg-sky-500/15 text-sky-300",
  relance: "bg-amber-400/15 text-amber-300",
  interesse: "bg-violet-500/15 text-violet-300",
  publie: "bg-emerald-500/15 text-emerald-300",
  refuse: "bg-destructive/20 text-red-300",
};
const EMPTY = { magazine: "", group: "", category: "agence", priority: 3, journalist: "", role: "",
  phone: "", mobile: "", email_direct: "", email_editorial: "", website: "", region: "", theme: "Pompes funèbres",
  audience: "", social: "", linkedin: "", first_contact_at: "", follow_up_at: "", status: "a_contacter", notes: "" };

const dstr = (v) => (v ? String(v).slice(0, 10) : "");

function parseCSV(text) {
  const rows = [];
  let i = 0, field = "", row = [], inQ = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { row.push(field); field = ""; }
      else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++;
        row.push(field); field = ""; if (row.some((c) => c !== "")) rows.push(row); row = [];
      } else field += ch;
    }
    i++;
  }
  if (field !== "" || row.length) { row.push(field); if (row.some((c) => c !== "")) rows.push(row); }
  return rows;
}

export function Funerals() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://bougieducoeur.com";
  const [data, setData] = useState({ contacts: [], counts: {}, categories: {}, statuses: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [scan, setScan] = useState(null);
  const scanTimer = useRef();
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat) params.category = cat;
      if (status) params.status = status;
      if (q) params.q = q;
      const r = await api.get("/admin/funeral-contacts", { params });
      setData(r.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, status]);

  const categories = data.categories || {};

  const openNew = () => { setForm(EMPTY); setEditOpen(true); };
  const openEdit = (c) => {
    setForm({ ...EMPTY, ...c, first_contact_at: dstr(c.first_contact_at), follow_up_at: dstr(c.follow_up_at) });
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.magazine.trim()) { toast.error("Le nom de la pompe funèbre est requis."); return; }
    setSaving(true);
    try {
      const payload = { ...form, priority: Number(form.priority) || 3 };
      if (form.id) await api.patch(`/admin/funeral-contacts/${form.id}`, payload);
      else await api.post("/admin/funeral-contacts", payload);
      toast.success(form.id ? "Fiche mise à jour" : "Pompe funèbre ajoutée");
      setEditOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette pompe funèbre ?")) return;
    try { await api.delete(`/admin/funeral-contacts/${id}`); setData((d) => ({ ...d, contacts: d.contacts.filter((c) => c.id !== id) })); toast.success("Fiche supprimée"); }
    catch { toast.error("Suppression impossible"); }
  };

  const invite = async (c) => {
    setInvitingId(c.id);
    try {
      const r = await api.post(`/admin/funeral-contacts/${c.id}/invite`, {});
      try { await navigator.clipboard?.writeText(r.data.link); } catch { /* ignore */ }
      toast.success(r.data.emailed ? "Invitation envoyée + lien copié (valable 3 jours)" : "Lien créé et copié (valable 3 jours)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setInvitingId(null); }
  };

  const copyLink = async (code) => {
    try { await navigator.clipboard.writeText(`${origin}/?amb=${code}`); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const inviteAll = async () => {
    const n = (data.contacts || []).length;
    if (!n) { toast.error("Aucune pompe funèbre à inviter"); return; }
    if (!window.confirm(`Envoyer une invitation ambassadeur (72 h, 1 bougie) à TOUTES les pompes funèbres du filtre actuel (${n}) qui ont un e-mail ?\nCelles déjà invitées ou sans e-mail seront automatiquement ignorées.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/funeral-contacts/invite-all", { category: cat || undefined, status: status || undefined, q: q || undefined });
      toast.success(`${r.data.invited} invitation(s) envoyée(s) · ${r.data.skipped_no_email} sans e-mail · ${r.data.skipped_already} déjà invité(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi groupé impossible"); }
    finally { setBulkBusy(false); }
  };

  const dedupe = async () => {
    if (!window.confirm("Supprimer les doublons (pompes funèbres de même nom) ?\nLa plus complète (ou déjà invitée) est conservée.")) return;
    try {
      const r = await api.post("/admin/funeral-contacts/dedupe", {});
      toast.success(r.data.removed ? `${r.data.removed} doublon(s) supprimé(s)` : "Aucun doublon trouvé");
      setDupOpen(false); load();
    } catch { toast.error("Déduplication impossible"); }
  };

  const detectDuplicates = async () => {
    setDupOpen(true); setDupLoading(true); setDupGroups([]);
    try { const r = await api.get("/admin/funeral-contacts/duplicates"); setDupGroups(r.data.groups || []); }
    catch { toast.error("Détection impossible"); }
    finally { setDupLoading(false); }
  };

  const toggleVerified = async (c) => {
    if (!(c.email_direct || c.email_editorial)) { toast.error("Aucun e-mail à contrôler"); return; }
    const next = !c.email_verified;
    try {
      await api.patch(`/admin/funeral-contacts/${c.id}`, { email_verified: next });
      setData((d) => ({ ...d, contacts: d.contacts.map((x) => x.id === c.id ? { ...x, email_verified: next } : x) }));
      toast.success(next ? "E-mail marqué comme contrôlé ✅" : "E-mail marqué à contrôler");
    } catch { toast.error("Action impossible"); }
  };

  const download = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
      URL.revokeObjectURL(href);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const startScanPoll = () => {
    scanTimer.current = setInterval(async () => {
      try {
        const r = await api.get("/admin/funeral-contacts/scan-status");
        setScan(r.data);
        if (!r.data.running) {
          clearInterval(scanTimer.current);
          toast.success(`Détection terminée : ${r.data.found} e-mail(s) trouvé(s)`);
          setScan(null);
          load();
        }
      } catch { clearInterval(scanTimer.current); setScan(null); }
    }, 3000);
  };

  const startScan = async () => {
    try {
      await api.post("/admin/funeral-contacts/scan-emails", {});
      setScan({ running: true, done: 0, total: 0, found: 0 });
      startScanPoll();
      toast.info("Détection des e-mails lancée… (cela peut prendre plusieurs minutes)");
    } catch (e) { toast.error(e.response?.data?.detail || "Détection impossible"); }
  };

  useEffect(() => () => clearInterval(scanTimer.current), []);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length < 2) { toast.error("Fichier CSV vide ou invalide"); return; }
    const header = rows[0].map((h) => h.trim());
    const objs = rows.slice(1).map((r) => { const o = {}; header.forEach((h, i) => { o[h] = (r[i] || "").trim(); }); return o; });
    try {
      const res = await api.post("/admin/funeral-contacts/import", { rows: objs });
      toast.success(`${res.data.inserted} pompe(s) funèbre(s) importée(s)${res.data.skipped ? ` · ${res.data.skipped} doublon(s) ignoré(s)` : ""}`);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Import impossible"); }
  };

  const filtered = useMemo(() => data.contacts || [], [data]);

  return (
    <div className="space-y-4" data-testid="funerals">
      <div className="rounded-3xl border border-amber-400/20 bg-amber-500/[0.05] p-5">
        <div className="flex items-center gap-2 text-amber-300"><Building2 size={18} /><h2 className="text-lg font-semibold">Pompes funèbres en France — CRM</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">
          {data.total} établissement(s) — réseaux nationaux préremplis. Ajoutez les agences locales par import CSV ou saisie, complétez les e-mails ou lancez la détection automatique.
          Invitez une pompe funèbre comme ambassadeur en un clic (lien unique <strong>valable 3 jours</strong>, 1 bougie offerte).
        </p>
      </div>

      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" className="rounded-full" onClick={openNew} data-testid="funeral-add-btn"><Plus size={15} className="mr-1.5" /> Ajouter</Button>
        <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => fileRef.current?.click()} data-testid="funeral-import-btn"><Upload size={15} className="mr-1.5" /> Importer CSV</Button>
        <Button size="sm" variant="outline" className="rounded-full border-amber-400/40 text-amber-200 hover:bg-amber-500/10" onClick={startScan} disabled={!!scan} data-testid="funeral-scan-btn">
          {scan ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Mail size={15} className="mr-1.5" />}
          {scan ? `Détection… ${scan.done}/${scan.total} · ${scan.found} trouvé(s)` : "Détecter les e-mails"}
        </Button>
        <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => download("/admin/funeral-contacts/export.csv", "pompes_funebres_france.csv")} data-testid="funeral-export-btn"><Download size={15} className="mr-1.5" /> Exporter CSV</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => download("/admin/funeral-contacts/template.csv", "modele_pompes_funebres.csv")} data-testid="funeral-template-btn"><FileText size={15} className="mr-1.5" /> Modèle CSV</Button>
        <Button size="sm" className="rounded-full bg-amber-500 text-black hover:bg-amber-400" onClick={inviteAll} disabled={bulkBusy} data-testid="funeral-invite-all-btn">
          {bulkBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Send size={15} className="mr-1.5" />} Inviter le filtre
        </Button>
        <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={detectDuplicates} data-testid="funeral-detect-dup-btn"><Copy size={15} className="mr-1.5" /> Détecter les doublons</Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} data-testid="funeral-import-input" />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setCat("")} className={`rounded-full border px-3 py-1 text-xs font-medium ${!cat ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/15 bg-white/5 text-muted-foreground"}`} data-testid="funeral-cat-all">Toutes ({data.total})</button>
        {Object.entries(categories).map(([k, label]) => (
          <button key={k} onClick={() => setCat(k)} className={`rounded-full border px-3 py-1 text-xs font-medium ${cat === k ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/15 bg-white/5 text-muted-foreground"}`} data-testid={`funeral-cat-${k}`}>
            {label} ({data.counts?.[k] || 0})
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Rechercher (nom, groupe, région, téléphone…)" className="pl-9" data-testid="funeral-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white" data-testid="funeral-status-filter">
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-neutral-900">{l}</option>)}
        </select>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-amber-300" /></div>
      ) : filtered.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="funeral-empty">Aucune pompe funèbre.</p>
      ) : (
        <div className="space-y-2" data-testid="funeral-list">
          {filtered.map((c) => (
            <div key={c.id} className="rounded-2xl border border-white/10 bg-black/20 p-3.5 transition-colors hover:border-white/25" data-testid="funeral-row">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openEdit(c)}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{c.magazine}</span>
                    {c.group && <span className="text-xs text-muted-foreground">· {c.group}</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status] || "bg-white/10"}`} data-testid="funeral-status-badge">{STATUS_LABELS[c.status] || c.status}</span>
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-muted-foreground">{categories[c.category] || c.category}</span>
                    {c.ambassador_code && <InviteCountdown expiresAt={c.follow_up_at} validated={c.validated} />}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    {c.phone && <span>{c.phone}</span>}
                    {(c.email_direct || c.email_editorial) && <span>{c.email_direct || c.email_editorial}</span>}
                    {c.region && <span>{c.region}</span>}
                    {c.notes && <span className="truncate max-w-[280px]">{c.notes}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <span className="mr-1 flex items-center gap-1.5" data-testid="funeral-contact-icons">
                    {c.website ? (
                      <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" title="Site web disponible" className="text-emerald-300 hover:text-emerald-200" data-testid="funeral-site-icon" onClick={(e) => e.stopPropagation()}><Globe size={15} /></a>
                    ) : (
                      <span title="Aucun site web" className="text-white/25" data-testid="funeral-site-icon-off"><Globe size={15} /></span>
                    )}
                    {(c.email_direct || c.email_editorial) ? (
                      <button type="button" onClick={() => toggleVerified(c)} title={c.email_verified ? "E-mail contrôlé — cliquer pour repasser « à contrôler »" : "E-mail présent, à contrôler — cliquer pour valider"} className={c.email_verified ? "text-emerald-300 hover:text-emerald-200" : "text-amber-300 hover:text-amber-200"} data-testid={c.email_verified ? "funeral-email-verified" : "funeral-email-unverified"}>
                        {c.email_verified ? <MailCheck size={15} /> : <MailWarning size={15} />}
                      </button>
                    ) : (
                      <span title="Aucun e-mail" className="text-white/25" data-testid="funeral-email-none"><MailX size={15} /></span>
                    )}
                  </span>
                  {c.ambassador_code ? (
                    <Button size="sm" variant="ghost" className="h-8 text-sky-300" onClick={() => copyLink(c.ambassador_code)} title="Copier le lien" data-testid="funeral-copy-link"><Copy size={14} /></Button>
                  ) : null}
                  <Button size="sm" variant="ghost" className="h-8 text-amber-300" disabled={invitingId === c.id} onClick={() => invite(c)} title="Inviter comme ambassadeur (72h)" data-testid="funeral-invite-btn">
                    {invitingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-foreground/70" onClick={() => openEdit(c)} data-testid="funeral-edit-btn"><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => remove(c.id)} data-testid="funeral-delete-btn"><Trash2 size={14} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog édition */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass max-h-[88vh] overflow-y-auto border-white/10 sm:max-w-2xl" data-testid="funeral-edit-dialog">
          <DialogHeader><DialogTitle className="text-lg font-medium">{form.id ? "Modifier la pompe funèbre" : "Nouvelle pompe funèbre"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 pt-1 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Nom de la pompe funèbre *</Label><Input value={form.magazine} onChange={(e) => setForm({ ...form, magazine: e.target.value })} data-testid="funeral-f-name" /></div>
            <div><Label>Groupe / réseau</Label><Input value={form.group || ""} onChange={(e) => setForm({ ...form, group: e.target.value })} data-testid="funeral-f-group" /></div>
            <div>
              <Label>Catégorie</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white" data-testid="funeral-f-category">
                {Object.entries(categories).map(([k, l]) => <option key={k} value={k} className="bg-neutral-900">{l}</option>)}
              </select>
            </div>
            <div>
              <Label>Statut</Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white" data-testid="funeral-f-status">
                {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-neutral-900">{l}</option>)}
              </select>
            </div>
            <div><Label>Responsable / contact</Label><Input value={form.journalist || ""} onChange={(e) => setForm({ ...form, journalist: e.target.value })} data-testid="funeral-f-contact" /></div>
            <div><Label>Fonction</Label><Input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="funeral-f-phone" /></div>
            <div><Label>Portable</Label><Input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label>E-mail direct</Label><Input value={form.email_direct || ""} onChange={(e) => setForm({ ...form, email_direct: e.target.value })} data-testid="funeral-f-email" /></div>
            <div><Label>E-mail (secondaire)</Label><Input value={form.email_editorial || ""} onChange={(e) => setForm({ ...form, email_editorial: e.target.value })} /></div>
            <div><Label>Site internet</Label><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
            <div><Label>Région / département</Label><Input value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} /></div>
            <div><Label>Date 1er contact</Label><Input type="date" value={form.first_contact_at || ""} onChange={(e) => setForm({ ...form, first_contact_at: e.target.value })} /></div>
            <div><Label>Date de relance</Label><Input type="date" value={form.follow_up_at || ""} onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Adresse / notes</Label><Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="funeral-f-notes" /></div>
          </div>
          <Button className="mt-2 w-full rounded-full" onClick={save} disabled={saving} data-testid="funeral-save-btn">
            {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Enregistrer
          </Button>
        </DialogContent>
      </Dialog>

      {/* Dialog détection des doublons */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="glass max-h-[80vh] overflow-y-auto border-white/10 sm:max-w-lg" data-testid="funeral-dup-dialog">
          <DialogHeader><DialogTitle className="text-lg font-medium">Doublons détectés</DialogTitle></DialogHeader>
          {dupLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-300" /></div>
          ) : dupGroups.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="funeral-dup-empty">Aucun doublon détecté. La liste est propre. ✅</p>
          ) : (
            <div className="space-y-2" data-testid="funeral-dup-list">
              <p className="text-sm text-muted-foreground">{dupGroups.length} nom(s) en double. La pompe funèbre la plus complète (ou déjà invitée) sera conservée.</p>
              {dupGroups.map((g, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm" data-testid="funeral-dup-row">
                  <span className="font-medium text-foreground">{g.name}</span>
                  <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] text-amber-300">{g.count} fiches</span>
                </div>
              ))}
              <Button className="mt-2 w-full rounded-full bg-amber-500 text-black hover:bg-amber-400" onClick={dedupe} data-testid="funeral-dup-dedupe-btn"><Trash2 size={15} className="mr-1.5" /> Supprimer les doublons</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Funerals;
